import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Skeleton,
} from '@parcel/ui';
import * as React from 'react';
import { apiErrorMessage } from '../../lib/api.js';
import { type Score, useSeedScores } from '../../lib/scores.js';
import { ScoringAnswersForm } from '../onboarding/scoring-answers-form.js';
import {
  type ScoringAnswers,
  answersFromScores,
  hasCriteria,
  toSeedBody,
  validateScoringAnswers,
} from '../onboarding/scoring-answers.js';

/** The onboarding answers again: seeding replaces the criteria on all three scores and recomputes. */
export function SeedDialog({
  workspaceId,
  cities,
  scores,
  failed,
  onRetry,
  open,
  onOpenChange,
  onSeeded,
}: {
  workspaceId: string | undefined;
  /** Undefined while loading; the form waits for both. */
  cities: readonly string[] | undefined;
  scores: readonly Score[] | undefined;
  /** The cities or the scores could not be read. */
  failed?: boolean;
  onRetry?: () => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSeeded: (scores: Score[]) => void;
}) {
  const seed = useSeedScores(workspaceId);
  return (
    <Dialog open={open} onOpenChange={(next) => !seed.isPending && onOpenChange(next)}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Set your market preferences</DialogTitle>
          <DialogDescription>
            Answer what you look for, and Parcel writes the criteria for all three scores. Edit them
            here afterwards.
          </DialogDescription>
        </DialogHeader>
        {/* The body mounts with each opening, so every opening starts from the current criteria. */}
        {cities && scores ? (
          <SeedDialogBody
            cities={cities}
            scores={scores}
            seed={seed}
            onCancel={() => onOpenChange(false)}
            onSeeded={(next) => {
              onSeeded(next);
              onOpenChange(false);
            }}
          />
        ) : failed ? (
          <p className="text-sm text-muted-foreground">
            Couldn't load your markets and scores.{' '}
            <button type="button" className="underline underline-offset-2" onClick={onRetry}>
              Retry
            </button>
          </p>
        ) : (
          <Skeleton className="h-64 w-full" />
        )}
      </DialogContent>
    </Dialog>
  );
}

function SeedDialogBody({
  cities,
  scores,
  seed,
  onCancel,
  onSeeded,
}: {
  cities: readonly string[];
  scores: readonly Score[];
  seed: ReturnType<typeof useSeedScores>;
  onCancel: () => void;
  onSeeded: (scores: Score[]) => void;
}) {
  const [initial] = React.useState(() => answersFromScores(scores, cities));
  const [answers, setAnswers] = React.useState<ScoringAnswers>(initial.answers);
  const [problem, setProblem] = React.useState<string | null>(null);
  const reset = seed.reset;
  React.useEffect(() => reset(), [reset]);

  const submit = () => {
    const issue = validateScoringAnswers(answers);
    setProblem(issue);
    if (issue) return;
    seed.mutate(toSeedBody(answers), { onSuccess: onSeeded });
  };

  return (
    <>
      <ScoringAnswersForm
        answers={answers}
        cities={cities}
        disabled={seed.isPending}
        inDialog
        onChange={setAnswers}
      />
      <div className="space-y-1 text-sm">
        {hasCriteria(scores) ? (
          <p className="font-medium">This replaces the criteria on all three scores.</p>
        ) : null}
        {!initial.readBack && hasCriteria(scores) ? (
          <p className="text-muted-foreground">
            Your current criteria were not set from answers, so these start from the defaults.
          </p>
        ) : null}
        {initial.lost.length > 0 ? (
          <p className="text-muted-foreground">
            Replacing also removes: {initial.lost.join('; ')}.
          </p>
        ) : null}
      </div>
      {problem || seed.error ? (
        <p className="text-sm text-destructive">
          {problem ?? apiErrorMessage(seed.error, "Couldn't seed the scores.")}
        </p>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button variant="outline" disabled={seed.isPending} onClick={onCancel}>
          Cancel
        </Button>
        <Button disabled={seed.isPending} onClick={submit}>
          {seed.isPending ? 'Writing…' : 'Write criteria'}
        </Button>
      </div>
    </>
  );
}
